import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { SafeIcon } from '@/components/ui/safe-icon';

export interface WeatherSettings {
  rainIntensity: number;
  rainSpeed: number;
  lightningFrequency: number;
  lightningIntensity: number;
  cloudSpeed: number;
  fogDensity: number;
  windStrength: number;
  masterVolume: number;
  rainVolume: number;
  thunderVolume: number;
  backgroundMusicVolume: number;
}

interface WeatherControlsProps {
  onSettingsChange: (settings: WeatherSettings) => void;
  isVisible: boolean;
  onToggleVisibility: () => void;
}

export const WeatherControls = ({ 
  onSettingsChange, 
  isVisible, 
  onToggleVisibility 
}: WeatherControlsProps) => {
  const [settings, setSettings] = useState<WeatherSettings>({
    rainIntensity: 100,
    rainSpeed: 50,
    lightningFrequency: 7,
    lightningIntensity: 75,
    cloudSpeed: 50,
    fogDensity: 50,
    windStrength: 30,
    masterVolume: 70,
    rainVolume: 60,
    thunderVolume: 80,
    backgroundMusicVolume: 50,
  });

  const updateSetting = (key: keyof WeatherSettings, value: number) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    onSettingsChange(newSettings);
  };

  const resetToDefault = () => {
    const defaultSettings: WeatherSettings = {
      rainIntensity: 100,
      rainSpeed: 50,
      lightningFrequency: 7,
      lightningIntensity: 75,
      cloudSpeed: 50,
      fogDensity: 50,
      windStrength: 30,
      masterVolume: 70,
      rainVolume: 60,
      thunderVolume: 80,
      backgroundMusicVolume: 50,
    };
    setSettings(defaultSettings);
    onSettingsChange(defaultSettings);
  };

  const applyPreset = (preset: 'calm' | 'moderate' | 'severe' | 'extreme') => {
    let presetSettings: WeatherSettings;
    
    switch (preset) {
      case 'calm':
        presetSettings = {
          rainIntensity: 20,
          rainSpeed: 20,
          lightningFrequency: 2,
          lightningIntensity: 30,
          cloudSpeed: 20,
          fogDensity: 30,
          windStrength: 10,
          masterVolume: 50,
          rainVolume: 30,
          thunderVolume: 40,
          backgroundMusicVolume: 60,
        };
        break;
      case 'moderate':
        presetSettings = {
          rainIntensity: 60,
          rainSpeed: 40,
          lightningFrequency: 5,
          lightningIntensity: 50,
          cloudSpeed: 40,
          fogDensity: 40,
          windStrength: 25,
          masterVolume: 60,
          rainVolume: 50,
          thunderVolume: 60,
          backgroundMusicVolume: 50,
        };
        break;
      case 'severe':
        presetSettings = {
          rainIntensity: 100,
          rainSpeed: 70,
          lightningFrequency: 10,
          lightningIntensity: 90,
          cloudSpeed: 70,
          fogDensity: 70,
          windStrength: 50,
          masterVolume: 80,
          rainVolume: 70,
          thunderVolume: 90,
          backgroundMusicVolume: 40,
        };
        break;
      case 'extreme':
        presetSettings = {
          rainIntensity: 150,
          rainSpeed: 100,
          lightningFrequency: 15,
          lightningIntensity: 100,
          cloudSpeed: 100,
          fogDensity: 90,
          windStrength: 80,
          masterVolume: 90,
          rainVolume: 80,
          thunderVolume: 100,
          backgroundMusicVolume: 30,
        };
        break;
    }
    
    setSettings(presetSettings);
    onSettingsChange(presetSettings);
  };

  return (
    <>
      {/* Toggle Button */}
      <Button
        onClick={onToggleVisibility}
        className="fixed top-4 right-4 z-50 bg-black/70 hover:bg-black/90 text-white border border-gray-600"
        size="sm"
      >
        <SafeIcon name={isVisible ? 'X' : 'Settings'} />
      </Button>

      {/* Controls Panel */}
      {isVisible && (
        <Card className="fixed top-16 right-4 z-40 w-80 max-h-[90vh] overflow-y-auto bg-black/80 backdrop-blur-md border border-gray-600 text-white">
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <SafeIcon name="CloudRain" />
                Weather & Audio Controls
              </h3>
            </div>

            {/* Weather Presets */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Weather Presets</label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => applyPreset('calm')}
                  variant="outline"
                  size="sm"
                  className="bg-blue-900/50 hover:bg-blue-800/70 border-blue-700"
                >
                  🌦️ Calm
                </Button>
                <Button
                  onClick={() => applyPreset('moderate')}
                  variant="outline"
                  size="sm"
                  className="bg-yellow-900/50 hover:bg-yellow-800/70 border-yellow-700"
                >
                  🌧️ Moderate
                </Button>
                <Button
                  onClick={() => applyPreset('severe')}
                  variant="outline"
                  size="sm"
                  className="bg-orange-900/50 hover:bg-orange-800/70 border-orange-700"
                >
                  ⛈️ Severe
                </Button>
                <Button
                  onClick={() => applyPreset('extreme')}
                  variant="outline"
                  size="sm"
                  className="bg-red-900/50 hover:bg-red-800/70 border-red-700"
                >
                  🌪️ Extreme
                </Button>
              </div>
            </div>

            {/* Audio Controls */}
            <div className="space-y-3 border-t border-gray-600 pt-4">
              <h4 className="text-md font-medium flex items-center gap-2">
                <SafeIcon name="Volume2" />
                Audio Controls
              </h4>
              
              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <SafeIcon name="Volume2" />
                  Master Volume: {settings.masterVolume}%
                </label>
                <Slider
                  value={[settings.masterVolume]}
                  onValueChange={(value) => updateSetting('masterVolume', value[0])}
                  max={100}
                  step={5}
                  className="mt-2"
                />
              </div>

              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  🎵 Background Music: {settings.backgroundMusicVolume}%
                </label>
                <Slider
                  value={[settings.backgroundMusicVolume]}
                  onValueChange={(value) => updateSetting('backgroundMusicVolume', value[0])}
                  max={100}
                  step={5}
                  className="mt-2"
                />
              </div>

              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <SafeIcon name="CloudRain" />
                  Rain Audio: {settings.rainVolume}%
                </label>
                <Slider
                  value={[settings.rainVolume]}
                  onValueChange={(value) => updateSetting('rainVolume', value[0])}
                  max={100}
                  step={5}
                  className="mt-2"
                />
              </div>

              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <SafeIcon name="Zap" />
                  Thunder Audio: {settings.thunderVolume}%
                </label>
                <Slider
                  value={[settings.thunderVolume]}
                  onValueChange={(value) => updateSetting('thunderVolume', value[0])}
                  max={100}
                  step={5}
                  className="mt-2"
                />
              </div>
            </div>

            {/* Rain Controls */}
            <div className="space-y-3 border-t border-gray-600 pt-4">
              <h4 className="text-md font-medium">Weather Effects</h4>
              
              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <SafeIcon name="CloudRain" />
                  Rain Intensity: {settings.rainIntensity}%
                </label>
                <Slider
                  value={[settings.rainIntensity]}
                  onValueChange={(value) => updateSetting('rainIntensity', value[0])}
                  max={200}
                  step={5}
                  className="mt-2"
                />
              </div>

              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <SafeIcon name="Zap" />
                  Rain Speed: {settings.rainSpeed}%
                </label>
                <Slider
                  value={[settings.rainSpeed]}
                  onValueChange={(value) => updateSetting('rainSpeed', value[0])}
                  max={200}
                  step={5}
                  className="mt-2"
                />
              </div>
            </div>

            {/* Lightning Controls */}
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <SafeIcon name="Zap" />
                  Lightning Frequency: {settings.lightningFrequency}
                </label>
                <Slider
                  value={[settings.lightningFrequency]}
                  onValueChange={(value) => updateSetting('lightningFrequency', value[0])}
                  max={20}
                  step={1}
                  className="mt-2"
                />
              </div>

              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <SafeIcon name="Sun" />
                  Lightning Intensity: {settings.lightningIntensity}%
                </label>
                <Slider
                  value={[settings.lightningIntensity]}
                  onValueChange={(value) => updateSetting('lightningIntensity', value[0])}
                  max={150}
                  step={5}
                  className="mt-2"
                />
              </div>
            </div>

            {/* Environment Controls */}
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <SafeIcon name="Cloud" />
                  Cloud Speed: {settings.cloudSpeed}%
                </label>
                <Slider
                  value={[settings.cloudSpeed]}
                  onValueChange={(value) => updateSetting('cloudSpeed', value[0])}
                  max={200}
                  step={5}
                  className="mt-2"
                />
              </div>

              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <SafeIcon name="Eye" />
                  Fog Density: {settings.fogDensity}%
                </label>
                <Slider
                  value={[settings.fogDensity]}
                  onValueChange={(value) => updateSetting('fogDensity', value[0])}
                  max={100}
                  step={5}
                  className="mt-2"
                />
              </div>

              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <SafeIcon name="Wind" />
                  Wind Strength: {settings.windStrength}%
                </label>
                <Slider
                  value={[settings.windStrength]}
                  onValueChange={(value) => updateSetting('windStrength', value[0])}
                  max={100}
                  step={5}
                  className="mt-2"
                />
              </div>
            </div>

            {/* Reset Button */}
            <Button
              onClick={resetToDefault}
              variant="outline"
              className="w-full bg-gray-800/50 hover:bg-gray-700/70 border-gray-600"
            >
              <SafeIcon name="RotateCcw" />
              Reset to Default
            </Button>
          </div>
        </Card>
      )}
    </>
  );
};